// src/prototype/components/ea_reports/use_case/EAUseCaseSectionBlock.tsx
// ============================================================
// DVA EA USE CASE ARTIFACT — SECTION BLOCK
// ============================================================
// Renders a single named section within a use-case artifact.
// Handles optional page-break-before for print pagination.
//
// `content` can be:
//   • a plain string (rendered as a paragraph)
//   • a React node (rendered as-is, giving full layout flexibility)
//
// The shell assigns sequential section numbers automatically.
// ============================================================

import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface ArtifactSection {
  /** Section heading displayed in the header bar */
  title: string;
  /**
   * Section body. Plain string renders as a paragraph.
   * Pass a React node for richer layouts (tables, metric rows, etc.)
   */
  content: string | React.ReactNode;
  /**
   * Force a print page break before this section.
   * Useful for Financial Impact, Recommendation, or Appendix pages.
   */
  pageBreakBefore?: boolean;
  /**
   * Prevent a page break from splitting the section body.
   * Default: true.
   */
  avoidBreak?: boolean;
}

export interface EAUseCaseSectionBlockProps extends ArtifactSection {
  /** 1-based section index, passed by the shell automatically */
  index: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export const EAUseCaseSectionBlock: React.FC<EAUseCaseSectionBlockProps> = ({
  title,
  content,
  pageBreakBefore = false,
  avoidBreak = true,
  index,
}) => {
  const sectionClass = [
    'ua-section',
    pageBreakBefore ? 'ua-page-break-before' : '',
    avoidBreak ? 'ua-avoid-break' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={sectionClass}>
      {/* ── Section Header ─────────────────────────────── */}
      <div className="ua-section-header">
        <div className="ua-section-icon">
          <span className="ua-section-number">{String(index).padStart(2, '0')}</span>
        </div>
        <div className="ua-section-title">{title}</div>
      </div>

      {/* ── Section Content ────────────────────────────── */}
      <div className="ua-section-content">
        {typeof content === 'string' ? <p>{content}</p> : content}
      </div>
    </div>
  );
};

export default EAUseCaseSectionBlock;

// ─────────────────────────────────────────────────────────────────────────────
// CONVENIENCE LAYOUT PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
// These can be used inside the `content` prop of any section for
// consistent rich layout without ad-hoc inline styles.

/** Coral-accented callout box. Use for key insights or warnings. */
export const UACallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="ua-callout">{children}</div>
);

/** Row of metric cards. Pass an array of { label, value } objects. */
export const UAMetricRow: React.FC<{
  metrics: { label: string; value: string }[];
}> = ({ metrics }) => (
  <div className="ua-metric-row">
    {metrics.map((m, i) => (
      <div key={i} className="ua-metric-card ua-avoid-break">
        <div className="ua-metric-card-label">{m.label}</div>
        <div className="ua-metric-card-value">{m.value}</div>
      </div>
    ))}
  </div>
);

/**
 * Three-column risk row: Risk / Impact / Mitigation.
 * Renders one identified risk in a scannable grid cell.
 */
export const UARiskRow: React.FC<{
  risk: string;
  impact: string;
  mitigation: string;
}> = ({ risk, impact, mitigation }) => (
  <div className="ua-risk-row ua-avoid-break">
    {[
      { label: 'Risk', value: risk },
      { label: 'Impact', value: impact },
      { label: 'Mitigation', value: mitigation },
    ].map(({ label, value }) => (
      <div key={label}>
        <div className="ua-risk-cell-label">{label}</div>
        <div className="ua-risk-cell-value">{value}</div>
      </div>
    ))}
  </div>
);